import { createReducer, on } from '@ngrx/store';
import { omit } from 'lodash-es';

import { BetslipBetBuilderPick } from '../../core/picks/betslip-bet-builder-pick';
import { BetslipPick } from '../../core/picks/betslip-pick';
import { BetslipV2ParticipantPick } from '../../core/picks/betslip-v2-participant-pick';
import { BetBuilderPickId } from '../../core/picks/pick-id';
import { PriceType, ResultStatus } from '../../core/picks/pick-models';
import BetslipPickActions from '../picks/actions';
import { EditBetActions } from './actions';
import { editBetUpdatedActionApplier, pickUpdatedActionApplier } from './actions-apply';
import { IEditMyBetPickState, defaultEditMyBetState } from './state';

export const editBetReducer = createReducer(
    defaultEditMyBetState,
    on(EditBetActions.init, (state, action) => {
        return defaultEditMyBetState;
    }),
    on(EditBetActions.setDefault, (state, action) => {
        return defaultEditMyBetState;
    }),
    on(BetslipPickActions.addPick, (state, action) => {
        const pick = action.pick;
        const pickId = pick.id.toString();

        return {
            ...state,
            picks: {
                ...state.picks,
                pickList: [...state.picks.pickList, pick],
                pickState: {
                    ...state.picks.pickState,
                    [pickId]: {
                        isRemoved: false,
                        resultedState: ResultStatus.Open,
                        canSwap: !(
                            (pick instanceof BetslipV2ParticipantPick && pick.participants.length > 1) ||
                            pick instanceof BetslipBetBuilderPick
                        ),
                        isHighlighted: false,
                    },
                },
            },
        };
    }),
    on(EditBetActions.addPick, (state, action) => {
        const pick = action.pick;
        const pickId = pick.id.toString();

        const existingPick = state.picks.pickList.find((p) => p.id.toString() === pickId);
        const existingPickState = existingPick && state.picks.pickState[pickId];

        if (existingPickState) {
            return state;
        }

        return {
            ...state,
            picks: {
                ...state.picks,
                pickList: [...state.picks.pickList, pick],
                pickState: {
                    ...state.picks.pickState,
                    [pickId]: {
                        isRemoved: false,
                        resultedState: ResultStatus.Open,
                        canSwap: !(
                            (pick instanceof BetslipV2ParticipantPick && pick.participants.length > 1) ||
                            pick instanceof BetslipBetBuilderPick
                        ),
                        isHighlighted: false,
                    },
                },
            },
        };
    }),
    on(EditBetActions.editBetUpdated, (state, action) => {
        const newState = {
            ...state,
            picks: {
                ...state.picks,
                pickList: editBetUpdatedActionApplier(action, state.picks.pickList),
            },
            addPicksState: {
                ...state.addPicksState,
                pickList: editBetUpdatedActionApplier(action, state.addPicksState.pickList),
            },
        };

        const earlyPayout = action.payload.earlyPayout;
        if (earlyPayout) {
            newState.earlyPayoutStake = earlyPayout.value != null ? earlyPayout.value : null;
            if (newState.isEarlyPayoutStakeLoading) {
                // isEarlyPayoutStakeLoading is true by default so if now is true this means it is first load.
                newState.acceptedEarlyPayoutStake = newState.earlyPayoutStake;
            }
            newState.isEarlyPayoutStakeLoading = false;
        }

        if (state.swapPickState.newPick !== null) {
            state.swapPickState.newPick = pickUpdatedActionApplier(action, state.swapPickState.newPick);
        }

        return newState;
    }),
    on(EditBetActions.disableEarlyPayout, (state) => {
        return {
            ...state,
            earlyPayoutStake: null,
        };
    }),
    on(EditBetActions.confirmAddingStake, (state, action) => {
        const stake = action.stake;

        return {
            ...state,
            addedStake: stake,
            confirmedStake: stake,
        };
    }),
    on(EditBetActions.setEditMyBetAddedStake, (state, action) => {
        return {
            ...state,
            addedStake: action.payload.stake,
            numpadAction: action.payload.action,
        };
    }),
    on(EditBetActions.fillEditMyBet, (state, action) => {
        const payload = action.payload;

        const pickList: BetslipPick[] = Object.keys(payload.pickState).map((ps) => payload.pickState[+ps].pick);

        const pickState = Object.keys(payload.pickState).reduce(
            (aggr, key) => {
                const pickState1 = payload.pickState[+key];
                aggr[pickState1.pick.id.toString()] = pickState1.state;

                return aggr;
            },
            {} as Record<string, IEditMyBetPickState>,
        );

        const betslip = payload.sourceBetslip;
        const earlyPayoutStake = betslip.earlyPayout?.value || null;

        return {
            ...state,
            betslipId: betslip.betslipRealId,
            picks: {
                pickList,
                pickState,
            },
            originalPicks: Object.keys(payload.pickState).map((ps) => payload.pickState[+ps].pick.id.toString()),
            acceptedEarlyPayoutStake: earlyPayoutStake,
            earlyPayoutStake,
            isEarlyPayoutStakeLoading: true,
        };
    }),
    on(EditBetActions.removePick, (state, action) => {
        const pickId = action.pickId;
        if (!state.picks.pickState[pickId]) {
            return state;
        }

        return {
            ...state,
            picks: {
                ...state.picks,
                pickState: {
                    ...state.picks.pickState,
                    [pickId]: {
                        ...state.picks.pickState[pickId],
                        isRemoved: true,
                    },
                },
            },
            addPicksState: {
                ...state.addPicksState,
                pickList: state.addPicksState.pickList.filter((p) => p.id.toString() !== pickId),
            },
        };
    }),
    on(EditBetActions.deletePick, (state, action) => {
        const pickId = action.pickId.toString();
        if (!state.picks.pickState[pickId]) {
            delete state.picks.pickState[pickId];
        }

        return {
            ...state,
            picks: {
                ...state.picks,
                pickList: state.picks.pickList.filter((p) => p.id.toString() !== pickId.toString()),
            },
            addPicksState: {
                ...state.addPicksState,
                pickList: state.addPicksState.pickList.filter((p) => p.id.toString() !== pickId.toString()),
            },
        };
    }),
    on(EditBetActions.undoRemovePick, (state, action) => {
        const pickId = action.pickId;
        if (!state.picks.pickState[pickId]) {
            return state;
        }

        return {
            ...state,
            picks: {
                ...state.picks,
                pickState: {
                    ...state.picks.pickState,
                    [pickId]: {
                        ...state.picks.pickState[pickId],
                        isRemoved: false,
                    },
                },
            },
        };
    }),
    on(EditBetActions.switchToAddSelectionMode, (state) => {
        return {
            ...state,
            addPicksState: {
                ...state.addPicksState,
                isVisible: true,
                pickList: [],
            },
        };
    }),
    on(EditBetActions.togglePicksContainer, (state) => {
        return {
            ...state,
            addPicksState: {
                ...state.addPicksState,
                isExpanded: !state.addPicksState.isExpanded,
            },
        };
    }),
    on(EditBetActions.cancelAddSelectionMode, EditBetActions.confirmAddSelectionMode, (state) => {
        return {
            ...state,
            addPicksState: defaultEditMyBetState.addPicksState,
        };
    }),
    on(EditBetActions.addPickToContainer, (state, action) => {
        const pick = action.pick;
        const pickState = state.addPicksState;
        if (!pickState.pickList.some((p) => p.id.toString() === pick.id.toString())) {
            return {
                ...state,
                addPicksState: {
                    ...state.addPicksState,
                    pickList: [...state.addPicksState.pickList, pick],
                },
            };
        }

        return state;
    }),
    on(EditBetActions.removeFromContainer, (state, action) => {
        const pickId = action.pickId;

        return {
            ...state,
            addPicksState: {
                ...state.addPicksState,
                pickList: state.addPicksState.pickList.filter((p) => p.id.toString() !== pickId),
            },
            picks: {
                ...state.picks,
                pickState: {
                    ...state.picks.pickState,
                    [pickId]: {
                        ...state.picks.pickState[pickId],
                        isRemoved: true,
                    },
                },
            },
        };
    }),
    on(EditBetActions.setFixedPrice, (state, action) => {
        state.addPicksState.pickList = state.addPicksState.pickList.map((pick) => {
            if (pick.id.isEqual(action.pickId)) {
                const newPick = pick.copy();
                newPick.priceType = PriceType.Fixed;
                newPick.acceptedPrice = { price: { ...newPick.currentPrice! }, isManuallyAccepted: true };

                return newPick;
            }

            return pick;
        });

        state.picks.pickList = state.picks.pickList.map((pick) => {
            if (pick.id.isEqual(action.pickId)) {
                const newPick = pick.copy();
                newPick.priceType = PriceType.Fixed;
                newPick.acceptedPrice = { price: { ...newPick.currentPrice! }, isManuallyAccepted: true };

                return newPick;
            }

            return pick;
        });

        return state;
    }),
    on(EditBetActions.setStartingPrice, (state, action) => {
        state.addPicksState.pickList = state.addPicksState.pickList.map((pick) => {
            if (pick.id.isEqual(action.pickId)) {
                const newPick = pick.copy();
                newPick.priceType = PriceType.StartingPrice;
                newPick.acceptedPrice = { price: { ...newPick.currentPrice! }, isManuallyAccepted: true };

                return newPick;
            }

            return pick;
        });

        state.picks.pickList = state.picks.pickList.map((pick) => {
            if (pick.id.isEqual(action.pickId)) {
                const newPick = pick.copy();
                newPick.priceType = PriceType.StartingPrice;
                newPick.acceptedPrice = { price: { ...newPick.currentPrice! }, isManuallyAccepted: true };

                return newPick;
            }

            return pick;
        });

        return state;
    }),
    on(EditBetActions.swapPick, (state, action) => {
        const { oldPick, newPick } = action.payload;
        const pickState = state.picks.pickState[oldPick.id.toString()];
        if (!pickState) {
            return state;
        }
        const isRemoved = pickState.isRemoved;
        const oldPickIndex = state.picks.pickList.indexOf(oldPick);
        const picksLength = state.picks.pickList.length;

        return {
            ...state,
            picks: {
                pickList: [...state.picks.pickList.slice(0, oldPickIndex), newPick, ...state.picks.pickList.slice(oldPickIndex + 1, picksLength)],
                pickState: {
                    ...omit(state.picks.pickState, oldPick.id.toString()),
                    [newPick.id.toString()]: {
                        isRemoved,
                        resultedState: ResultStatus.Open,
                        canSwap: true,
                        isHighlighted: true,
                    },
                },
            },
            swapPickState: {
                oldPick: null,
                newPick: null,
            },
        };
    }),
    on(EditBetActions.switchToSwapSelectionMode, (state, action) => {
        const pick = action.pick;

        return {
            ...state,
            swapPickState: {
                ...state.swapPickState,
                oldPick: pick,
            },
        };
    }),
    on(EditBetActions.addPickToSwap, EditBetActions.updatePickToSwap, (state, action) => {
        const pick = action.pick;

        return {
            ...state,
            swapPickState: {
                ...state.swapPickState,
                newPick: pick,
            },
        };
    }),
    on(EditBetActions.cancelSwapSelectionMode, (state) => {
        return {
            ...state,
            swapPickState: {
                oldPick: null,
                newPick: null,
            },
        };
    }),
    on(EditBetActions.acceptCurrentEarlyPayoutValue, (state) => {
        return {
            ...state,
            acceptedEarlyPayoutStake: state.earlyPayoutStake,
        };
    }),
    on(BetslipPickActions.acceptCurrentPrice, (state, action) => {
        const pickIds = action.pickIds;
        state.addPicksState.pickList = state.addPicksState.pickList.map((pick) => {
            if (pickIds.some((id) => id.isEqual(pick.id))) {
                const newPick = pick.copy();
                newPick.acceptedPrice = {
                    price: { ...newPick.currentPrice! },
                    isManuallyAccepted: action.isManuallyAccepted,
                };

                return newPick;
            }

            return pick;
        });

        state.picks.pickList = state.picks.pickList
            .filter((pick) => !BetBuilderPickId.isId(pick.id))
            .map((pick) => {
                if (pickIds.some((id) => id.isEqual(pick.id))) {
                    const newPick = pick.copy();
                    newPick.acceptedPrice = {
                        price: { ...newPick.currentPrice! },
                        isManuallyAccepted: action.isManuallyAccepted,
                    };

                    return newPick;
                }

                return pick;
            });

        return state;
    }),

    on(EditBetActions.applyEditBetPickResults, (state, action) => {
        const payload = action.payload;
        const pickState = state.picks.pickState[payload.pickId.toString()];
        if (pickState) {
            return {
                ...state,
                picks: {
                    ...state.picks,
                    pickState: {
                        ...state.picks.pickState,
                        [payload.pickId.toString()]: {
                            ...state.picks.pickState[payload.pickId.toString()],
                            resultedState: payload.newState,
                        },
                    },
                },
            };
        }

        return state;
    }),
    on(BetslipPickActions.removePick, (state, action) => {
        const pickId = action.pickId;

        return {
            ...state,
            picks: {
                ...state.picks,
                pickList: state.picks.pickList.filter((p) => p.id.toString() !== pickId.toString()),
                pickState: omit(state.picks.pickState, action.pickId.toString()),
            },
            addPicksState: {
                ...state.addPicksState,
                pickList: state.addPicksState.pickList.filter((p) => p.id.toString() !== pickId.toString()),
            },
        };
    }),
);
